import { formatDistanceToNow } from 'date-fns';
import { Briefcase, CheckCircle2, Drama, Eye, EyeOff, Mic, PlayCircle, Scale, Search, XCircle } from 'lucide-react';

export const QUALITY_POPUP_KEY = 'quality_popup_dismissed';

export const KIND_ORDER = ['recruiter', 'roleplay', 'job_simulation', 'sourcing_lab', 'eeoc'];

export const KIND_META = {
    recruiter: { label: 'Recruiter Simulations', short: 'Recruiter Sim', icon: Mic, tone: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-300', testId: 'recruiter' },
    roleplay: { label: 'Role Play Lab', short: 'Role Play', icon: Drama, tone: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300', testId: 'roleplay' },
    job_simulation: { label: 'Job Simulations', short: 'Job Sim', icon: Briefcase, tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-300', testId: 'jobsims' },
    sourcing_lab: { label: 'Sourcing Labs', short: 'Sourcing Lab', icon: Search, tone: 'bg-teal-500/10 text-teal-600 dark:text-teal-300', testId: 'sourcing' },
    eeoc: { label: 'EEOC', short: 'EEOC', icon: Scale, tone: 'bg-sky-500/10 text-sky-600 dark:text-sky-300', testId: 'eeoc' },
};

export const STATUS_META = {
    not_started: { label: 'Not started', className: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300' },
    in_progress: { label: 'In progress', className: 'border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-800/60 dark:bg-sky-950/60 dark:text-sky-300' },
    completed: { label: 'Completed', className: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/50 dark:text-emerald-300' },
    missed: { label: 'Missed', className: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/50 dark:text-rose-300' },
};

export const STAGE_ORDER = ['not_opened', 'opened', 'in_progress', 'completed', 'missed'];

export const STAGE_META = {
    not_opened: { label: 'Not opened', icon: EyeOff, bar: 'bg-slate-400 dark:bg-slate-500', text: 'text-slate-600 dark:text-slate-300', className: STATUS_META.not_started.className },
    opened: { label: 'Opened', icon: Eye, bar: 'bg-indigo-500', text: 'text-indigo-600 dark:text-indigo-300', className: 'border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-800/60 dark:bg-indigo-950/60 dark:text-indigo-300' },
    in_progress: { label: 'In progress', icon: PlayCircle, bar: 'bg-sky-500', text: 'text-sky-600 dark:text-sky-300', className: STATUS_META.in_progress.className },
    completed: { label: 'Completed', icon: CheckCircle2, bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', className: STATUS_META.completed.className },
    missed: { label: 'Missed', icon: XCircle, bar: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400', className: STATUS_META.missed.className },
};

export const timeAgo = (iso) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : '');

export const REVIEW_CYCLE_LABELS = { weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly' };

export const isOpen = (task) => task.status === 'not_started' || task.status === 'in_progress';

export const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export const timeLeftLabel = (hoursLeft) => {
    if (hoursLeft <= 0) return 'Deadline passed';
    if (hoursLeft < 1) return `${Math.max(1, Math.round(hoursLeft * 60))} min left`;
    if (hoursLeft < 48) return `${Math.round(hoursLeft)}h left`;
    return `${Math.round(hoursLeft / 24)} days left`;
};

export const scoreTone = (score) => {
    if (score === null || score === undefined) return 'text-muted-foreground';
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 60) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
};

export const endOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 0);
    return d;
};

export const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

export const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
